import { hasCognitoGroupName } from '../../../../util/user';
import { AWS_GROUP_NAMES } from '../../../../util/permissions/constants';

export const FAVOURITES_STORAGE_KEY = 'cg_favourites';
export const PAGINATION_BAR_STORAGE_KEY = 'cg_pagination_bar_collapsed';
export const PROJECT_PAGE_SIZE_OPTIONS = [50, 100, 200, 500, 1000, 'all'];
export const DEFAULT_PAGE_SIZE = 50;
export const ALL_PROJECTS_PAGINATION_STORAGE_KEY = 'cg_all_projects_pagination';
export const MY_PROJECTS_PAGINATION_STORAGE_KEY = 'cg_my_projects_pagination';
export const TRACKING_PAGINATION_STORAGE_KEY = 'cg_tracking_pagination';

const normalizePageSize = value => {
	if (value === 'all') {return 'all';}
	const numericValue = Number(value);
	return PROJECT_PAGE_SIZE_OPTIONS.includes(numericValue)
		? numericValue
		: DEFAULT_PAGE_SIZE;
};

const normalizeCurrentPage = value => {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) && numericValue > 0
		? Math.floor(numericValue)
		: 1;
};

export const isInternalProjectsUser = () =>
	hasCognitoGroupName(AWS_GROUP_NAMES.AIP_ADMINS);

// theme-kit's Dropdown calls onChange with the raw value, not an event, but
// the shared filter handlers (handleTypeFilter etc.) also back real <select>
// elements elsewhere and expect e.target.value — wrap rather than change them.
export const asChangeEvent = handler => value => handler({ target: { value } });

const projectFavouriteIds = id => {
	const match = String(id || '').match(/^corrmonitor:[^:]+::(.+)$/);
	return match ? [id, `corrmonitor:${match[1]}`] : [id];
};

export const isProjectFavourite = (favourites, id) =>
	projectFavouriteIds(id).some(key => favourites.has(key));

export const toggleProjectFavourite = (favourites, id) => {
	const next = new Set(favourites);
	if (isProjectFavourite(next, id)) {
		projectFavouriteIds(id).forEach(key => next.delete(key));
	} else {
		next.add(id);
	}
	return next;
};

export const loadPaginationBarCollapsed = () => {
	try {
		const value = localStorage.getItem(PAGINATION_BAR_STORAGE_KEY);
		return value === null ? true : value === 'true';
	} catch {
		return true;
	}
};

export const persistPaginationBarCollapsed = isCollapsed => {
	try {
		localStorage.setItem(PAGINATION_BAR_STORAGE_KEY, String(isCollapsed));
	} catch {
		// ignore
	}
};

export const loadProjectFavourites = () => {
	try {
		return new Set(JSON.parse(localStorage.getItem(FAVOURITES_STORAGE_KEY) || '[]'));
	} catch {
		return new Set();
	}
};

export const persistProjectFavourites = favourites => {
	try {
		localStorage.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify([...favourites]));
	} catch {
		// ignore
	}
};

export const loadProjectPaginationState = storageKey => {
	try {
		const value = JSON.parse(localStorage.getItem(storageKey) || '{}');
		return {
			currentPage: normalizeCurrentPage(value.currentPage),
			pageSize:    normalizePageSize(value.pageSize)
		};
	} catch {
		return {
			currentPage: 1,
			pageSize:    DEFAULT_PAGE_SIZE
		};
	}
};

export const persistProjectPaginationState = (storageKey, { currentPage, pageSize }) => {
	try {
		localStorage.setItem(storageKey, JSON.stringify({
			currentPage: normalizeCurrentPage(currentPage),
			pageSize:    normalizePageSize(pageSize)
		}));
	} catch {
		// ignore
	}
};

export const getProjectPaginationData = (items, currentPage, pageSize) => {
	const totalItems = items.length;
	const normalizedPageSize = normalizePageSize(pageSize);
	const effectivePageSize = normalizedPageSize === 'all'
		? Math.max(totalItems, 1)
		: normalizedPageSize;
	const totalPages = Math.max(1, Math.ceil(totalItems / effectivePageSize));
	const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
	const startIndex = totalItems === 0 ? 0 : (safeCurrentPage - 1) * effectivePageSize;
	const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + effectivePageSize, totalItems);

	return {
		currentPage: safeCurrentPage,
		endItem: endIndex,
		items: items.slice(startIndex, endIndex),
		pageSizeValue: normalizedPageSize,
		startItem: totalItems === 0 ? 0 : startIndex + 1,
		totalItems,
		totalPages
	};
};
